import { useEffect, useRef, useState } from "react";
import type { Editor } from "@tiptap/react";
import { IconNoteImage } from "./icons";
import { insertImageFromFile } from "./pasteImage";

/**
 * Dropdown/popover menu contents for the note toolbar. Each returns its list
 * of actions only; the surrounding .nt-pop card + motion is rendered by the
 * toolbar's <ToolbarPop> so every popover shares the app's dropdown language.
 * Every action focuses the editor first so the stored selection is restored
 * and typing continues where the user left off.
 */

export const FONT_SIZES = [10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48];
export const LINE_HEIGHTS = [
  { value: "1", label: "Single" },
  { value: "1.15", label: "1.15" },
  { value: "1.5", label: "1.5" },
  { value: "2", label: "Double" },
];
export const TEXT_COLORS = [
  "#f2f2f5", "#c9c9d4", "#8a8a99", "#55555f", "#111114",
  "#ef4444", "#f97316", "#fbbf24", "#22c55e", "#2dd4bf",
  "#3b82f6", "#a855f7", "#ec4899", "#f43f5e",
];
export const HIGHLIGHT_COLORS = [
  "#fde047", "#fb923c", "#f87171", "#4ade80",
  "#60a5fa", "#c084fc", "#f472b6", "#e2e8f0",
];

function MenuButton({ label, active = false, onClick }: { label: string; active?: boolean; onClick: () => void }) {
  return (
    <button type="button" className={`nt-menu-btn${active ? " is-active" : ""}`} onClick={onClick}>
      {label}
    </button>
  );
}

function currentFontSize(editor: Editor): number | null {
  const raw = editor.getAttributes("textStyle").fontSize as string | undefined;
  const px = raw ? parseInt(raw, 10) : NaN;
  return Number.isFinite(px) && FONT_SIZES.includes(px) ? px : null;
}

export function FontSizeMenu({ editor, onClose }: { editor: Editor; onClose: () => void }) {
  const current = currentFontSize(editor);
  return (
    <>
      {FONT_SIZES.map((px) => (
        <MenuButton
          key={px}
          label={String(px)}
          active={current === px}
          onClick={() => {
            editor.chain().focus().setFontSize(`${px}px`).run();
            onClose();
          }}
        />
      ))}
    </>
  );
}

export function ColorMenu({ editor, onClose }: { editor: Editor; onClose: () => void }) {
  const current = editor.getAttributes("textStyle").color as string | undefined;
  return (
    <>
      <div className="nt-pop-label">Text colour</div>
      <div className="nt-swatch-row">
        <button
          type="button"
          className={`nt-swatch nt-swatch-none${current ? "" : " is-active"}`}
          title="Default colour"
          aria-label="Default colour"
          onClick={() => {
            editor.chain().focus().unsetColor().run();
            onClose();
          }}
        />
        {TEXT_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            className={`nt-swatch${current === c ? " is-active" : ""}`}
            style={{ background: c }}
            title={c}
            aria-label={`Text colour ${c}`}
            onClick={() => {
              editor.chain().focus().setColor(c).run();
              onClose();
            }}
          />
        ))}
      </div>
    </>
  );
}

export function HighlightMenu({ editor, onClose }: { editor: Editor; onClose: () => void }) {
  const current = editor.getAttributes("highlight").color as string | undefined;
  return (
    <>
      <div className="nt-pop-label">Highlight colour</div>
      <div className="nt-swatch-row">
        <button
          type="button"
          className={`nt-swatch nt-swatch-none${current ? "" : " is-active"}`}
          title="No highlight"
          aria-label="No highlight"
          onClick={() => {
            editor.chain().focus().unsetHighlight().run();
            onClose();
          }}
        />
        {HIGHLIGHT_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            className={`nt-swatch${current === c ? " is-active" : ""}`}
            style={{ background: c }}
            title={c}
            aria-label={`Highlight ${c}`}
            onClick={() => {
              editor.chain().focus().toggleHighlight({ color: c }).run();
              onClose();
            }}
          />
        ))}
      </div>
    </>
  );
}

export function SpacingMenu({ editor, onClose }: { editor: Editor; onClose: () => void }) {
  const lineHeight = editor.getAttributes("paragraph").lineHeight as string | undefined;
  const spacingBefore = (editor.getAttributes("paragraph").spacingBefore as number | undefined) ?? 0;
  const spacingAfter = (editor.getAttributes("paragraph").spacingAfter as number | undefined) ?? 0;
  return (
    <>
      <div className="nt-pop-label">Line spacing</div>
      {LINE_HEIGHTS.map(({ value, label }) => (
        <MenuButton
          key={value}
          label={label}
          active={lineHeight === value}
          onClick={() => {
            editor.chain().focus().setLineHeight(value).run();
            onClose();
          }}
        />
      ))}
      <div className="nt-pop-label">Paragraph spacing</div>
      <MenuButton
        label="Space before paragraph"
        active={spacingBefore > 0}
        onClick={() => {
          editor.chain().focus().toggleSpacingBefore().run();
          onClose();
        }}
      />
      <MenuButton
        label="Space after paragraph"
        active={spacingAfter > 0}
        onClick={() => {
          editor.chain().focus().toggleSpacingAfter().run();
          onClose();
        }}
      />
    </>
  );
}

export function AlignMenu({ editor, onClose }: { editor: Editor; onClose: () => void }) {
  const options = [
    { value: "left", label: "Align left" },
    { value: "center", label: "Align centre" },
    { value: "right", label: "Align right" },
    { value: "justify", label: "Justify" },
  ];
  return (
    <>
      <div className="nt-pop-label">Alignment</div>
      {options.map(({ value, label }) => (
        <MenuButton
          key={value}
          label={label}
          active={editor.isActive({ textAlign: value })}
          onClick={() => {
            editor.chain().focus().toggleTextAlign(value).run();
            onClose();
          }}
        />
      ))}
    </>
  );
}

/** Normalise a user-typed URL: keep explicit protocols, default to https://. */
function normalizeHref(raw: string): string | null {
  const value = raw.trim();
  if (!value) return null;
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return value;
  return `https://${value}`;
}

export function LinkPopover({ editor, onClose }: { editor: Editor; onClose: () => void }) {
  const existing = (editor.getAttributes("link").href as string | undefined) ?? "";
  const [href, setHref] = useState(existing);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const apply = () => {
    const target = normalizeHref(href);
    if (target) {
      editor.chain().focus().extendMarkRange("link").setLink({ href: target }).run();
    }
    onClose();
  };

  return (
    <>
      <input
        ref={inputRef}
        className="nt-link-input"
        value={href}
        placeholder="https://example.com"
        aria-label="Link URL"
        onChange={(e) => setHref(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") apply();
          if (e.key === "Escape") onClose();
        }}
      />
      <div className="nt-pop-actions">
        <button type="button" className="nt-menu-btn is-primary" onClick={apply} disabled={!href.trim()}>
          Apply
        </button>
        <button
          type="button"
          className="nt-menu-btn"
          onClick={() => {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            onClose();
          }}
          disabled={!editor.isActive("link")}
        >
          Remove
        </button>
      </div>
    </>
  );
}

export function ImageMenuButton({ editor }: { editor: Editor }) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) insertImageFromFile(editor, file);
          e.target.value = "";
        }}
      />
      <button type="button" className="nt-btn" title="Insert image" aria-label="Insert image" onClick={() => inputRef.current?.click()}>
        <IconNoteImage />
      </button>
    </>
  );
}